// Prompt drafting for an approved concept: create versioned drafts and submit a
// version into the image-request review queue. Shown only for approved concepts.

import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { IMAGE_ASPECTS, IMAGE_TIERS } from '@commerce/shared';
import { api, ApiClientError } from '../lib/api.js';
import type { ImageRequest, PromptDraft } from '../lib/types.js';

export function ConceptPrompts({ conceptId }: { conceptId: string }) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({
    prompt: '',
    aspectRatio: IMAGE_ASPECTS[0] as string,
    imageModelTier: IMAGE_TIERS[1] as string,
    negativeConstraints: '',
  });

  const drafts = useQuery({
    queryKey: ['prompt-drafts', conceptId],
    queryFn: () => api.get<PromptDraft[]>(`/prompt-drafts?conceptId=${conceptId}`),
  });

  const create = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post<PromptDraft>('/prompt-drafts', body),
    onSuccess: async () => {
      setForm({ ...form, prompt: '', negativeConstraints: '' });
      await queryClient.invalidateQueries({ queryKey: ['prompt-drafts', conceptId] });
    },
    onError: (err) => setError(err instanceof ApiClientError ? err.message : 'Failed to save'),
  });

  const submit = useMutation({
    mutationFn: (promptDraftId: string) =>
      api.post<ImageRequest>('/image-requests', { promptDraftId }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['image-requests'] }),
    onError: (err) => setError(err instanceof ApiClientError ? err.message : 'Submit failed'),
  });

  function onCreate(e: FormEvent) {
    e.preventDefault();
    setError(null);
    create.mutate({
      conceptId,
      prompt: form.prompt,
      aspectRatio: form.aspectRatio,
      imageModelTier: form.imageModelTier,
      negativeConstraints: form.negativeConstraints
        .split(',')
        .map((c) => c.trim())
        .filter(Boolean),
    });
  }

  return (
    <div className="review-panel">
      <h2>Prompt drafts</h2>
      <form className="form" onSubmit={onCreate}>
        <label htmlFor="prompt">Prompt (original artwork only — no brands or imitation)</label>
        <textarea
          id="prompt"
          rows={3}
          value={form.prompt}
          onChange={(e) => setForm({ ...form, prompt: e.target.value })}
          required
        />
        <div className="filters">
          <label>
            Aspect
            <select
              value={form.aspectRatio}
              onChange={(e) => setForm({ ...form, aspectRatio: e.target.value })}
            >
              {IMAGE_ASPECTS.map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </select>
          </label>
          <label>
            Tier
            <select
              value={form.imageModelTier}
              onChange={(e) => setForm({ ...form, imageModelTier: e.target.value })}
            >
              {IMAGE_TIERS.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </label>
        </div>
        <label htmlFor="neg">Negative constraints (comma-separated)</label>
        <input
          id="neg"
          value={form.negativeConstraints}
          onChange={(e) => setForm({ ...form, negativeConstraints: e.target.value })}
        />
        {error && (
          <p role="alert" className="form-error">
            {error}
          </p>
        )}
        <button type="submit" disabled={create.isPending}>
          Save prompt version
        </button>
      </form>

      {drafts.data && drafts.data.data.length > 0 && (
        <table className="data-table">
          <thead>
            <tr>
              <th>Version</th>
              <th>Prompt</th>
              <th>Aspect / tier</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {drafts.data.data.map((d) => (
              <tr key={d.id}>
                <td>v{d.version}</td>
                <td>{d.prompt}</td>
                <td>
                  {d.aspectRatio} / {d.imageModelTier}
                </td>
                <td>
                  <button
                    type="button"
                    onClick={() => submit.mutate(d.id)}
                    disabled={submit.isPending}
                  >
                    Submit for review
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
